import { Controller } from "@hotwired/stimulus";
import { placesChanged } from "../events/places";
import { throttle } from "underscore";
import { t } from "../i18n/t";

export default class extends Controller {
  static targets = [
    "field",
    "map",
    "latitude",
    "longitude",
    "address1",
    "address2",
    "zip",
    "cityName",
    "areaName",
    "countryName",
    "countryCode",
    "countryId",
    "viewport",
  ];

  static values = {
    country: String,
    forceSelect: Boolean,
    autocompleteTypes: {
      type: Array,
      default: ["locality", "administrative_area_level_3", "sublocality_level_2", "shopping_mall", "sublocality"],
    },
  };

  connect() {
    this.handlePageShow = this.handlePageShow.bind(this);
    window.addEventListener("pageshow", this.handlePageShow);

    if (typeof google != "undefined") {
      this.initializeMap();
    }

    if (this.forceSelectValue) {
      this.addCustomParsleyValidation();
    }
  }

  disconnect() {
    window.removeEventListener("pageshow", this.handlePageShow);
  }

  initializeMap() {
    let latitude = this.latitudeTarget.value;
    let longitude = this.longitudeTarget.value;
    let position = new google.maps.LatLng(latitude, longitude);
    let autocompleteOptions = {};

    if (this.countryValue) {
      autocompleteOptions = {
        types: this.autocompleteTypesValue,
        componentRestrictions: { country: this.countryValue },
      };
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.fieldTarget, autocompleteOptions);
    this.autocomplete.setFields(["address_components", "geometry", "icon", "name"]);
    this.autocomplete.addListener("place_changed", this.placeChanged.bind(this));

    if (this.hasMapTarget) {
      this.map = new google.maps.Map(this.mapTarget, {
        center: position,
        zoom: latitude == null ? 4 : 15,
      });
      this.autocomplete.bindTo("bounds", this.map);
      this.marker = new google.maps.Marker({
        map: this.map,
      });

      this.marker.setPosition(position);
      this.marker.setVisible(true);
    }

    this.fieldTarget.addEventListener("keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    });

    if (this.enableDropdownUpdateOnScroll) {
      window.addEventListener("scroll", throttle(this.updateDropdownPosition.bind(this), 5));
    }
  }

  placeChanged() {
    let place = this.autocomplete.getPlace();

    if (!place.geometry) return;

    this.setAddressResults(place);

    if (this.hasMapTarget) {
      if (place.geometry.viewport) {
        this.map.fitBounds(place.geometry.viewport);
      } else {
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(17);
      }

      this.marker.setPosition(place.geometry.location);
      this.marker.setVisible(true);
    }

    if (!this.disablePlaceChangedEvent) {
      placesChanged(place);
    }
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }

  setAddressResults(result) {
    let street_number = "",
      address = "",
      level1 = "",
      level2 = "",
      level3 = "",
      locality = "",
      neighborhood = "",
      sublocality = "",
      sublocality_level_1 = "",
      country = "",
      country_code = "",
      postal_code = "",
      format = "";
    //console.log(result);
    for (let i = 0; i < result.address_components.length; i++) {
      const addr = result.address_components[i];
      if (addr.types.indexOf("country") !== -1) {
        country = addr.long_name;
        country_code = addr.short_name;
      } else if (addr.types[0] === "street_address") address = address + addr.long_name;
      else if (addr.types[0] === "route") address = address + addr.long_name;
      else if (addr.types[0] === "street_number") street_number = addr.long_name;
      else if (addr.types[0] === "administrative_area_level_1") level1 = addr.long_name;
      else if (addr.types[0] === "administrative_area_level_2") level2 = addr.short_name;
      else if (addr.types[0] === "administrative_area_level_3") level3 = addr.short_name;
      else if (addr.types[0] === "locality") locality = addr.long_name;
      else if (addr.types[0] === "postal_code") postal_code = addr.long_name;
      else if (addr.types[0] === "sublocality") sublocality = addr.short_name;
      else if (addr.types[0] === "sublocality_level_1") sublocality_level_1 = addr.short_name;
      else if (addr.types[0] === "neighborhood") neighborhood = addr.short_name;
    }

    this.latitudeTarget.value = result.geometry.location.lat();
    this.longitudeTarget.value = result.geometry.location.lng();
    this.viewportTarget.value = [
      result.geometry.viewport.getSouthWest().lat(),
      result.geometry.viewport.getSouthWest().lng(),
      result.geometry.viewport.getNorthEast().lat(),
      result.geometry.viewport.getNorthEast().lng(),
    ];

    if (this.forceSelectValue) {
      this.resetParsleyValidation();
    }

    if (this.hasAddress1Target) {
      this.address1Target.value = ("" + address + " " + street_number).trim();
    }

    if (this.hasAddress2Target) {
      this.address2Target.value = "";
    }

    if (this.hasZipTarget) {
      this.zipTarget.value = "" + postal_code;
    }

    if (this.hasCityNameTarget) {
      this.cityNameTarget.value = "" + locality || sublocality || level3 || level2;
    }

    if (this.hasCountryCodeTarget) {
      this.countryCodeTarget.value = "" + country_code;
    }

    if (this.hasCountryNameTarget) {
      this.countryNameTarget.value = "" + country;
    }

    if (this.hasCountryIdTarget) {
      $.ajax({
        url: this.countryIdTarget.dataset.collectionUrl,
        type: "GET",
        dataType: "json",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }).done((data) => {
        function getIdByCountryCode(country_code) {
          const found = data.find((item) => item.code === country_code);
          return found ? found.id : null;
        }
        let countryId = getIdByCountryCode(country_code);
        if (countryId) {
          let newOption = new Option(country, countryId, false, true);
          $("#" + this.countryIdTarget.id)
            .append(newOption)
            .trigger("change");
          document.querySelector("#" + this.countryIdTarget.id + " option").value = countryId;
          document.querySelector(`span[aria-labelledby="select2-${this.countryIdTarget.id}-container"] input`).value =
            country;
        }
      });
    }
  }

  updateDropdownPosition() {
    const rect = this.fieldTarget.getBoundingClientRect();
    const dropdowns = Array.from(document.querySelectorAll(".pac-container")).filter(
      (dropdown) => dropdown.style.display !== "none"
    );

    if (dropdowns.length > 0) {
      const dropdown = dropdowns[0];
      dropdown.style.top = `${rect.bottom + window.scrollY}px`;
      dropdown.style.left = `${rect.left + window.scrollX}px`;

      for (let i = 1; i < dropdowns.length; i++) {
        dropdowns[i].style.display = "none";
      }
    }
  }

  addCustomParsleyValidation() {
    if (typeof window.Parsley === "undefined") return;

    // Prevent duplicate validator registration
    const registry = window.Parsley?._validatorRegistry?.validators;
    if (!registry?.forcePlaceSelect) {
      window.Parsley.addValidator("forcePlaceSelect", {
        requirementType: "string",
        validateString: () => {
          return this.viewportTarget.value.trim() !== "";
        },
      });
    }

    this.fieldTarget.setAttribute("data-parsley-force-place-select", "true");
    this.fieldTarget.setAttribute("data-parsley-validate-if-empty", "true");
    this.fieldTarget.setAttribute(
      "data-parsley-force-place-select-message",
      t("parsley.force_place_select", document.documentElement.lang)
    );

    const $form = $(this.fieldTarget).closest("form");
    $form.parsley();
  }

  resetParsleyValidation() {
    if (typeof window.Parsley !== "undefined") {
      $(this.fieldTarget).closest("form").parsley().reset();
    }
  }

  handlePageShow(event) {
    if (event.persisted) {
      window.location.reload();
    }
  }

  get disablePlaceChangedEvent() {
    return this.data.get("disablePlaceChangedEvent") === "true";
  }

  get enableDropdownUpdateOnScroll() {
    return this.data.get("enableDropdownUpdateOnScroll") === "true";
  }
}
